import Api from '../Api';
import { stripJsonFromPastedText } from './organizationAutofill';

/**
 * Список мероприятий без категории (скрытые / на модерации) + категории афиш.
 */
export async function fetchPendingCategoryBulkPublishData() {
  const [{ data: pendingData }, { data: catData }] = await Promise.all([
    Api.getAdminEventsPendingCategory(),
    Api.getEventCategories(),
  ]);

  if (pendingData?.status !== 'ok') {
    const msg = typeof pendingData?.message === 'string' ? pendingData.message : 'Не удалось получить список мероприятий.';
    throw new Error(msg);
  }

  const rawEvents = pendingData.events
    ?? pendingData.data?.events
    ?? pendingData.pending_events
    ?? [];

  const events = Array.isArray(rawEvents) ? rawEvents : [];
  const categories = catData?.categories || [];

  return { events, categories };
}

function eventForAiReference(ev) {
  if (!ev || typeof ev !== 'object') return null;
  const desc = ev.description != null ? String(ev.description) : '';
  return {
    _id: ev._id,
    name: ev.name != null ? String(ev.name) : '',
    specialization: ev.specialization != null ? String(ev.specialization) : '',
    holding_date: ev.holding_date != null ? String(ev.holding_date) : '',
    description: desc.length > 2500 ? `${desc.slice(0, 2500)}…` : desc,
  };
}

export function buildEventCategoryBulkPublishFileContent({ events, categories }) {
  const referenceData = {
    event_categories: categories
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((c) => ({
        _id: c._id,
        name: c.name,
        sort: c.sort,
      })),
    events_pending_category: events.map(eventForAiReference).filter(Boolean),
  };

  const exampleResponseShape = {
    items: [
      {
        event_id: '000000000000000000000001',
        events_category_id: '0000000000000000000000aa',
        events_category_name: 'Название категории (как в event_categories.name)',
        event_name: 'Название мероприятия (как в events_pending_category.name)',
      },
      {
        event_id: '000000000000000000000002',
        events_category_id: '0000000000000000000000bb',
        events_category_name: 'Другое название категории',
        event_name: 'Title мероприятия из списка',
      },
    ],
  };

  const schemaDescription = {
    description: 'Один JSON-объект с массивом items: для каждого мероприятия из events_pending_category выбери ровно одну категорию из event_categories по смыслу названия и описания.',
    root_shape: '{ "items": [ ... ] }',
    expected_response_example: exampleResponseShape,
    item_fields: {
      event_id: 'строка — ровно _id мероприятия из events_pending_category',
      events_category_id: 'строка — ровно _id категории из event_categories',
      events_category_name: 'строка — человекочитаемое name выбранной категории (как в справочнике; для сверки с events_category_id)',
      event_name: 'строка — название мероприятия (как в events_pending_category.name / title; для сверки)',
    },
    json_syntax: 'Ответ одним сообщением: ТОЛЬКО блок ```json … ``` — внутри один валидный JSON с ключом items, как после JSON.stringify, для последующего JSON.parse. Никакого текста до ```json и после ```. В строковых значениях внутренняя кавычка только как \\".',
    rules: [
      'КРИТИЧНО: количество элементов в items должно быть строго равно количеству элементов во входном массиве REFERENCE_DATA.events_pending_category (ни пропусков, ни лишних строк).',
      'Для каждого события из events_pending_category верни ровно один объект в items.',
      'Используй только категории из REFERENCE_DATA.event_categories. Не выдумывай _id (цифры в expected_response_example — заглушки).',
      'Если категория неочевидна — наиболее близкая по смыслу; если ничего не подходит — категория с name «Другое» из того же списка.',
      'Каждый event_id из events_pending_category встречается в items ровно один раз (без дубликатов).',
      'Перед отправкой ответа проверь: (1) все _id из events_pending_category покрыты в items; (2) нет дубликатов event_id; (3) items.length === events_pending_category.length.',
      'events_category_name должно буквально совпадать с name выбранной категории из REFERENCE_DATA.event_categories для того же events_category_id.',
      'event_name должно совпадать с name мероприятия из REFERENCE_DATA.events_pending_category для того же event_id.',
    ],
  };

  const expectedFormatBlock = [
    '────────── ОЖИДАЕМЫЙ_ФОРМАТ_ОТВЕТА (пример: id и имена заменить на реальные из REFERENCE_DATA ниже) ──────────',
    JSON.stringify(exampleResponseShape, null, 2),
    '────────── конец примера ──────────',
  ].join('\n');

  const prompt = [
    'Ты — помощник администратора Nomad: нужно назначить категории афишам, которые пришли с парсинга и ещё не имеют категории.',
    '',
    '────────── Ответ оператору ──────────',
    'Оператор пришлёт тебе этот файл одним сообщением. Сразу после получения файла верни ответ ТОЛЬКО в виде одного блока кода с типом json (см. «КРИТИЧНО — формат ответа» ниже): без приветствий и без текста вне блока.',
    '',
    '────────── Инструкция (устойчивый промпт) ──────────',
    'Ты должен сопоставить каждому мероприятию из REFERENCE_DATA.events_pending_category ровно одну категорию из REFERENCE_DATA.event_categories.',
    '',
    'Правила:',
    'КРИТИЧНО — формат ответа:',
    '— Всегда возвращай результат ТОЛЬКО внутри блока кода с типом json:',
    '```json',
    '{...}',
    '```',
    '— Без текста до открывающего ```json и после закрывающих ```.',
    '— Внутри фигурных скобок — один валидный JSON с корнем вида {"items":[...]}.',
    '— КРИТИЧНО: любые двойные кавычки внутри текстовых полей ответа (например в event_name или events_category_name, если в них есть символ ") экранируй как \\" — как в результате JSON.stringify; не вставляй голые кавычки внутри уже открытой строки JSON.',
    '— Для каждого входного события верни ровно один объект в items. Нельзя пропускать события.',
    '— КРИТИЧНО: количество элементов в items должно быть строго равно длине массива REFERENCE_DATA.events_pending_category.',
    '— Используй только категории из REFERENCE_DATA.event_categories (только реальные _id и name из этого списка).',
    '— Если категория неочевидна — выбери наиболее близкую по смыслу.',
    '— Если ничего явно не подходит — используй категорию с полем name «Другое» (возьми её _id из того же event_categories).',
    '— В ответе не должно быть пояснений вне блока ```json; внутри блока — только валидный JSON, корень с видом {"items":[...]}.',
    '',
    'Перед отправкой ответа обязательно проверь:',
    '1) что обработаны все event_id из events_pending_category (каждый _id события есть в items ровно один раз);',
    '2) что нет дубликатов event_id в items;',
    '3) что длина items равна длине events_pending_category.',
    '',
    '────────── Данные ──────────',
    'Ниже один объект REFERENCE_DATA: в нём ключи event_categories и events_pending_category (это и есть входные «данные категорий» и «данные мероприятий» для текущего набора).',
    '',
    '────────── Формат ответа ──────────',
    'См. блок «ОЖИДАЕМЫЙ_ФОРМАТ_ОТВЕТА» в этом файле и SCHEMA_HINT.expected_response_example. Поля каждого элемента items:',
    '— event_id — _id мероприятия;',
    '— events_category_id — _id выбранной категории;',
    '— events_category_name — name этой категории из event_categories (для сверки);',
    '— event_name — name мероприятия из events_pending_category.',
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — категории для афиш на модерации (данные + промпт для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Нажмите в админке «Скачать файл для ИИ» — подтянутся категории и мероприятия без категории (GET /api/admins/events/pending-category).',
    '2) Ниже в файле есть блок «ОЖИДАЕМЫЙ_ФОРМАТ_ОТВЕТА» — пример JSON, который должна вернуть модель (вместо заглушек — ваши реальные _id и имена из REFERENCE_DATA).',
    '3) Одним сообщением отправьте языковой модели весь этот файл целиком. Модель должна вернуть ответ только внутри блока ```json … ```.',
    '4) Скопируйте ответ модели целиком в поле справа (блок допустим; система подставляет JSON для отправки на сервер). Перед отправкой убедитесь, что в items столько же строк, сколько мероприятий в файле.',
    '5) Если ответ ИИ не принимается системой — открой https://jsonviewer.stack.hu/, вставь текст, нажми «Format», исправь структуру при необходимости и скопируй JSON обратно.',
    '',
    expectedFormatBlock,
    '',
    prompt,
  ].join('\n');
}

function pickId(obj, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null && String(obj[k]).trim()) {
      return String(obj[k]).trim();
    }
  }
  return '';
}

/**
 * Текст успешной модалки после POST /api/admins/events/bulk-category-publish
 */
export function formatBulkCategoryPublishSuccessMessage(apiResponse) {
  if (!apiResponse || typeof apiResponse !== 'object') {
    return 'Запрос выполнен успешно.';
  }

  const lines = ['Запрос выполнен успешно.'];

  const {
    requested,
    matched,
    modified,
    operation_id: operationId,
    not_found: notFound,
    pending_events: pendingEvents,
  } = apiResponse;

  if (typeof requested === 'number') {
    lines.push(`В запросе позиций: ${requested}.`);
  }
  if (typeof matched === 'number') {
    lines.push(`Сопоставлено с базой: ${matched}.`);
  }
  if (typeof modified === 'number') {
    lines.push(`Обновлено мероприятий: ${modified}.`);
  }
  if (operationId) {
    lines.push(`ID операции: ${operationId}.`);
  }

  if (Array.isArray(notFound) && notFound.length > 0) {
    lines.push(`Не найдены на сервере (not_found): ${notFound.length}.`);
  }

  const before = pendingEvents && typeof pendingEvents.before === 'number' ? pendingEvents.before : null;
  const after = pendingEvents && typeof pendingEvents.after === 'number' ? pendingEvents.after : null;

  if (before !== null && after !== null) {
    lines.push('');
    lines.push(`Ожидающих категории мероприятий было: ${before}, стало: ${after}.`);
    if (after > 0) {
      lines.push('');
      lines.push('Остались мероприятия без категории — можно повторить эту операцию: снова скачайте файл для ИИ и пройдите шаги, чтобы «добить» оставшиеся.');
    }
  }

  return lines.join('\n');
}

/**
 * Разбор ответа ИИ → payload для POST /api/admins/events/bulk-category-publish
 */
export function parseBulkCategoryPublishItems(jsonString) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  let rows = [];
  if (Array.isArray(data)) {
    rows = data;
  } else if (data && typeof data === 'object' && Array.isArray(data.items)) {
    rows = data.items;
  } else {
    return { error: 'Ожидается объект { "items": [...] } или массив объектов.' };
  }

  const items = [];
  const hex24 = /^[a-f\d]{24}$/i;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row || typeof row !== 'object') {
      return { error: `Элемент ${i + 1}: ожидался объект.` };
    }
    const eventId = pickId(row, 'event_id', 'eventId');
    const categoryId = pickId(row, 'events_category_id', 'eventscategory_id', 'eventsCategoryId');
    if (!eventId || !categoryId) {
      return { error: `Элемент ${i + 1}: нужны event_id и events_category_id.` };
    }
    if (!hex24.test(eventId) || !hex24.test(categoryId)) {
      return { error: `Элемент ${i + 1}: event_id и events_category_id должны быть 24-символьными hex-идентификаторами.` };
    }
    const categoryName = pickId(row, 'events_category_name', 'eventsCategoryName');
    const eventName = pickId(row, 'event_name', 'eventName', 'title');
    if (!categoryName || !eventName) {
      return { error: `Элемент ${i + 1}: укажите events_category_name и event_name (для сверки).` };
    }
    items.push({ event_id: eventId, events_category_id: categoryId });
  }

  if (!items.length) {
    return { error: 'Массив items пуст.' };
  }

  return { items };
}
